/**
 * GenericModulePage — reusable admin CRUD page backed by the
 * `/api/admin/modules/{module_key}` backend router.
 *
 * Used by all former "Coming soon" placeholder routes so editors see a
 * fully functional list/create/edit/delete UI with storage in MongoDB.
 *
 * Props:
 *   moduleKey   : string  (required — storage partition key in DB)
 *   title       : string  (page heading)
 *   description : string  (short paragraph under the heading)
 *   icon        : lucide-react component (header icon)
 *   fields      : optional array of extra field descriptors to show in the
 *                  create/edit form. Each descriptor: { key, label, type,
 *                  placeholder, options? }  type ∈ text|textarea|number|url|select
 *   newLabel    : string  (label on the "+ New" button, defaults to "Novi unos")
 *   emptyHint   : string  (hint text when there are zero items)
 */
import { useCallback, useEffect, useState } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { Plus, Trash2, Pencil, X, Check, Hourglass, Archive, Filter, Globe, EyeOff, Share2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Stat({ label, value, color = 'text-zinc-100' }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-3">
      <p className="text-[10px] uppercase text-zinc-500">{label}</p>
      <p className={`text-xl font-bold ${color}`}>{value}</p>
    </div>
  );
}

export default function GenericModulePage({
  moduleKey,
  title,
  description,
  icon: Icon,
  fields = [],
  newLabel = 'Novi unos',
  emptyHint = 'Još nema unosa. Kliknite gornje dugme za dodavanje prvog.',
}) {
  const { authFetch } = useAdmin();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, draft: 0, archived: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [editing, setEditing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listR, statsR] = await Promise.all([
        authFetch(`${API}/admin/modules/${moduleKey}/items?status=${filter}&limit=200`).then(r => r.json()),
        authFetch(`${API}/admin/modules/${moduleKey}/stats`).then(r => r.json()),
      ]);
      setItems(listR.items || []);
      setStats(statsR || {});
    } catch (e) {
      toast.error('Greška pri učitavanju');
    } finally {
      setLoading(false);
    }
  }, [authFetch, moduleKey, filter]);

  useEffect(() => { load(); }, [load]);

  async function handleSave(payload) {
    const url = editing
      ? `${API}/admin/modules/${moduleKey}/items/${editing.id}`
      : `${API}/admin/modules/${moduleKey}/items`;
    const method = editing ? 'PUT' : 'POST';
    const r = await authFetch(url, { method, body: JSON.stringify(payload) });
    if (r.ok) {
      toast.success(editing ? 'Ažurirano' : 'Spremljeno');
      setModalOpen(false);
      setEditing(null);
      load();
    } else {
      toast.error('Spremanje nije uspjelo');
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Obrisati ovaj unos?')) return;
    const r = await authFetch(`${API}/admin/modules/${moduleKey}/items/${id}`, { method: 'DELETE' });
    if (r.ok) { toast.success('Obrisano'); load(); }
  }

  async function handleStatus(id, status) {
    const r = await authFetch(`${API}/admin/modules/${moduleKey}/items/${id}`, {
      method: 'PUT', body: JSON.stringify({ status }),
    });
    if (r.ok) load();
  }

  async function handlePublishPublic(id, publish_public) {
    const r = await authFetch(`${API}/admin/modules/${moduleKey}/items/${id}/publish`, {
      method: 'POST', body: JSON.stringify({ publish_public }),
    });
    if (r.ok) {
      toast.success(publish_public ? 'Objavljeno na portalu' : 'Uklonjeno s portala');
      load();
    } else {
      toast.error('Promjena javne vidljivosti nije uspjela');
    }
  }

  return (
    <div className="p-6" data-testid={`module-${moduleKey}`}>
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            {Icon && <Icon size={22} className="text-red-400" />} {title}
          </h1>
          {description && <p className="text-xs text-zinc-400 mt-1 max-w-2xl">{description}</p>}
        </div>
        <button
          onClick={() => { setEditing(null); setModalOpen(true); }}
          className="px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 rounded-md text-white flex items-center gap-1.5 shrink-0"
          data-testid={`module-${moduleKey}-new`}
        >
          <Plus size={14} /> {newLabel}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <Stat label="Ukupno" value={stats.total || 0} />
        <Stat label="Aktivni" value={stats.active || 0} color="text-emerald-400" />
        <Stat label="Nacrt" value={stats.draft || 0} color="text-amber-400" />
        <Stat label="Arhivirano" value={stats.archived || 0} color="text-zinc-500" />
        <Stat label="Javno" value={stats.public || 0} color="text-sky-400" />
      </div>

      <div className="flex items-center gap-2 mb-4 text-xs">
        <Filter size={12} className="text-zinc-500" />
        {['all', 'active', 'draft', 'archived'].map(s => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-2.5 py-1 rounded ${filter === s ? 'bg-red-600 text-white' : 'bg-zinc-900 text-zinc-400 border border-zinc-800 hover:border-zinc-700'}`}
            data-testid={`module-${moduleKey}-filter-${s}`}
          >
            {({ all: 'Sve', active: 'Aktivni', draft: 'Nacrt', archived: 'Arhiva' })[s]}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-12 text-center text-zinc-500 text-sm flex items-center justify-center gap-2">
          <Hourglass size={14} className="animate-spin" /> Učitavanje…
        </div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-zinc-800 rounded-lg p-10 text-center">
          <p className="text-sm text-zinc-500">{emptyHint}</p>
        </div>
      ) : (
        <div className="border border-zinc-800 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900 text-zinc-400 text-left text-xs">
              <tr>
                <th className="px-3 py-2">Naslov</th>
                <th className="px-3 py-2 hidden md:table-cell">Opis</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-center">Javno</th>
                <th className="px-3 py-2 hidden lg:table-cell">Ažurirano</th>
                <th className="px-3 py-2 text-right">Akcije</th>
              </tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it.id} className="border-t border-zinc-800 hover:bg-zinc-900/30" data-testid={`module-${moduleKey}-row-${it.id}`}>
                  <td className="px-3 py-2 font-medium text-zinc-100">{it.title}</td>
                  <td className="px-3 py-2 text-zinc-400 text-xs max-w-xs truncate hidden md:table-cell">{it.description}</td>
                  <td className="px-3 py-2">
                    <select
                      value={it.status || 'active'}
                      onChange={e => handleStatus(it.id, e.target.value)}
                      className="bg-zinc-800 text-xs rounded px-1.5 py-0.5 border border-zinc-700"
                      data-testid={`module-${moduleKey}-status-${it.id}`}
                    >
                      <option value="active">aktivan</option>
                      <option value="draft">nacrt</option>
                      <option value="archived">arhiviran</option>
                    </select>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <button
                      onClick={() => handlePublishPublic(it.id, !it.publish_public)}
                      className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded ${it.publish_public ? 'bg-sky-500/15 text-sky-300 border border-sky-500/40' : 'bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-sky-500/40'}`}
                      data-testid={`module-${moduleKey}-publish-${it.id}`}
                      title={it.publish_public ? 'Klikni za povlačenje s portala' : 'Klikni za objavu na portalu'}
                    >
                      {it.publish_public ? (<><Globe size={11} /> Javno</>) : (<><EyeOff size={11} /> Skriveno</>)}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-xs text-zinc-500 hidden lg:table-cell">
                    {it.updated_at ? new Date(it.updated_at).toLocaleString('hr-HR') : '—'}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {it.data?.share_url && (
                      <a
                        href={it.data.share_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-2 py-1 mr-1 text-[11px] font-semibold rounded bg-emerald-600/20 text-emerald-300 hover:bg-emerald-600/40 border border-emerald-500/40"
                        title={`Objavi na ${it.data.network || 'mreži'} — otvara composer`}
                        data-testid={`module-${moduleKey}-share-${it.id}`}
                      >
                        <Share2 size={11} /> Objavi
                      </a>
                    )}
                    <button
                      onClick={() => { setEditing(it); setModalOpen(true); }}
                      className="p-1.5 text-blue-400 hover:text-blue-300" title="Uredi"
                      data-testid={`module-${moduleKey}-edit-${it.id}`}
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => handleStatus(it.id, it.status === 'archived' ? 'active' : 'archived')}
                      className="p-1.5 text-zinc-400 hover:text-zinc-200"
                      title={it.status === 'archived' ? 'Vrati iz arhive' : 'Arhiviraj'}
                    >
                      <Archive size={14} />
                    </button>
                    <button
                      onClick={() => handleDelete(it.id)}
                      className="p-1.5 text-red-400 hover:text-red-300" title="Obriši"
                      data-testid={`module-${moduleKey}-delete-${it.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && (
        <ItemModal
          moduleKey={moduleKey}
          initial={editing}
          fields={fields}
          onClose={() => { setModalOpen(false); setEditing(null); }}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

function ItemModal({ moduleKey, initial, fields, onClose, onSave }) {
  const [title, setTitle] = useState(initial?.title || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [status, setStatus] = useState(initial?.status || 'active');
  const [publishPublic, setPublishPublic] = useState(!!initial?.publish_public);
  const [extra, setExtra] = useState(initial?.data || {});

  function setExtraField(key, value) {
    setExtra(prev => ({ ...prev, [key]: value }));
  }

  function submit(e) {
    e.preventDefault();
    if (!title.trim()) return;
    onSave({ title: title.trim(), description, status, data: extra, publish_public: publishPublic });
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={e => e.stopPropagation()}
        className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-lg p-6 max-h-[90vh] overflow-auto"
        data-testid={`module-${moduleKey}-modal`}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{initial ? 'Uredi unos' : 'Novi unos'}</h3>
          <button type="button" onClick={onClose} className="p-1.5 rounded hover:bg-zinc-800 text-zinc-400">
            <X size={18} />
          </button>
        </div>

        <label className="block text-xs text-zinc-400 mb-1">Naslov *</label>
        <input
          required value={title} onChange={e => setTitle(e.target.value)}
          className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm mb-3"
          data-testid={`module-${moduleKey}-input-title`}
        />

        <label className="block text-xs text-zinc-400 mb-1">Kratak opis</label>
        <textarea
          value={description} onChange={e => setDescription(e.target.value)} rows={3}
          className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm mb-3"
          data-testid={`module-${moduleKey}-input-description`}
        />

        {fields.map(f => (
          <div key={f.key} className="mb-3">
            <label className="block text-xs text-zinc-400 mb-1">{f.label}</label>
            {f.type === 'textarea' ? (
              <textarea
                value={extra[f.key] || ''} onChange={e => setExtraField(f.key, e.target.value)} rows={3}
                placeholder={f.placeholder}
                className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm"
                data-testid={`module-${moduleKey}-input-${f.key}`}
              />
            ) : f.type === 'select' ? (
              <select
                value={extra[f.key] || ''} onChange={e => setExtraField(f.key, e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm"
                data-testid={`module-${moduleKey}-input-${f.key}`}
              >
                <option value="">—</option>
                {(f.options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            ) : (
              <input
                type={f.type || 'text'} value={extra[f.key] || ''}
                onChange={e => setExtraField(f.key, f.type === 'number' ? (+e.target.value || 0) : e.target.value)}
                placeholder={f.placeholder}
                className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm"
                data-testid={`module-${moduleKey}-input-${f.key}`}
              />
            )}
          </div>
        ))}

        <label className="block text-xs text-zinc-400 mb-1">Status</label>
        <select
          value={status} onChange={e => setStatus(e.target.value)}
          className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm mb-4"
          data-testid={`module-${moduleKey}-input-status`}
        >
          <option value="active">Aktivan</option>
          <option value="draft">Nacrt</option>
          <option value="archived">Arhiviran</option>
        </select>

        <label className="flex items-start gap-2 text-sm mb-4 cursor-pointer select-none bg-zinc-950/50 border border-zinc-800 rounded-md p-3">
          <input
            type="checkbox"
            checked={publishPublic}
            onChange={e => setPublishPublic(e.target.checked)}
            className="mt-0.5 accent-sky-500"
            data-testid={`module-${moduleKey}-input-publish-public`}
          />
          <span>
            <span className="font-medium text-sky-300">Objavi na javnom portalu</span>
            <span className="block text-[11px] text-zinc-500 mt-0.5">
              Breaking unosi idu u naslovnu vrpcu, oglasni slotovi u javni oglasni prostor, footer partneri u donji dio stranice.
            </span>
          </span>
        </label>

        <button
          type="submit"
          className="w-full py-2 bg-red-600 hover:bg-red-700 rounded-md font-medium flex items-center justify-center gap-1.5"
          data-testid={`module-${moduleKey}-save`}
        >
          <Check size={14} /> {initial ? 'Spremi promjene' : 'Dodaj'}
        </button>
      </form>
    </div>
  );
}
